import axios from '../api/axios';

export interface ShipReview {
  id: number;
  shipId: number;
  userId: number;
  username: string;
  rating: number;
  reviewText?: string;
  upvoteCount: number;
  downvoteCount: number;
  helpfulnessScore: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewSummary {
  averageRating: number;
  totalReviews: number;
  distribution: Record<number, number>;
}

export interface ReviewsResponse {
  reviews: ShipReview[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  summary: ReviewSummary;
  currentUserReviewId?: number;
}

export interface CreateReviewDto {
  rating: number;
  reviewText?: string;
}

export interface VoteResponse {
  upvoteCount: number;
  downvoteCount: number;
  helpfulnessScore: number;
}

export type SortOption = 'helpful' | 'newest' | 'oldest' | 'highest' | 'lowest';

class ShipReviewService {
  /**
   * Get reviews for a ship with pagination and sorting
   */
  async getReviews(
    shipId: number,
    page: number = 1,
    pageSize: number = 20,
    sortBy: SortOption = 'helpful'
  ): Promise<ReviewsResponse> {
    const response = await axios.get(`/ships/${shipId}/reviews`, {
      params: { page, pageSize, sortBy }
    });
    return response.data;
  }

  /**
   * Create a new review for a ship
   */
  async createReview(shipId: number, data: CreateReviewDto): Promise<ShipReview> {
    const response = await axios.post(`/ships/${shipId}/reviews`, data);
    return response.data;
  }

  /**
   * Update an existing review
   */
  async updateReview(shipId: number, reviewId: number, data: CreateReviewDto): Promise<ShipReview> {
    const response = await axios.put(`/ships/${shipId}/reviews/${reviewId}`, data);
    return response.data;
  }

  /**
   * Delete a review
   */
  async deleteReview(shipId: number, reviewId: number): Promise<void> {
    await axios.delete(`/ships/${shipId}/reviews/${reviewId}`);
  }

  /**
   * Vote on a review (upvote or downvote)
   */
  async voteOnReview(shipId: number, reviewId: number, isUpvote: boolean): Promise<VoteResponse> {
    const response = await axios.post(`/ships/${shipId}/reviews/${reviewId}/vote`, {
      isUpvote
    });
    return response.data;
  }

  /**
   * Get user's vote status for all reviews on a ship
   */
  async getMyVotes(shipId: number): Promise<Record<number, boolean>> {
    const response = await axios.get(`/ships/${shipId}/reviews/my-votes`);
    return response.data;
  }
}

export const shipReviewService = new ShipReviewService();
export default shipReviewService;
